import { useEffect, useRef, useState } from "react"
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine"
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { disableNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview"
import { attachClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge"
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge"
import type { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/types"
import type { DragData } from "./dragData"
import type { DragKind } from "./dragKind"
import type { DropZone } from "./dropZone"
import { offCanvas } from "./offCanvas"

/**
 * Makes an item in an ordered list draggable, and a place to drop on its top
 * or bottom edge. It reports what the item shows while a drag is on: whether
 * it is the one dragged, which edge a drop would land on, and whether letting
 * go now would take it off the canvas. What a drop does is the screen's to
 * decide (see the drag-and-drop pattern).
 *
 * An item that gives `handleRef` an element is dragged only by that, its
 * drag handle; one that doesn't is dragged from anywhere on it.
 *
 * The browser's own picture of the item is hidden, because it can't show the
 * item coming off: the item must render a DragPreview, or nothing follows the
 * pointer.
 */
export function useSortableItem<E extends HTMLElement>({
  id,
  kind,
  zone,
  accepts,
}: {
  id: string
  /** What the item is while it is dragged. */
  kind: DragKind<string>
  /** What the item is as a place to drop on. */
  zone: DropZone<string>
  /** Which drags may land on the item. */
  accepts: (source: DragData) => boolean
}) {
  const ref = useRef<E>(null)
  const handleRef = useRef<HTMLElement>(null)
  const [dragging, setDragging] = useState(false)
  const [dropEdge, setDropEdge] = useState<Edge | null>(null)
  const [removing, setRemoving] = useState(false)
  // The copy is moved by writing to it directly, not through state: a render
  // on every frame of a drag is what this avoids.
  const preview = useRef<HTMLElement | null>(null)
  const pointer = useRef<Input>({ clientX: 0, clientY: 0 })
  const grip = useRef<Grip>({ x: 0, y: 0, width: 0 })

  useEffect(() => {
    const element = ref.current!
    return combine(
      draggable({
        element,
        dragHandle: handleRef.current ?? undefined,
        getInitialData: () => kind.data(id),
        onGenerateDragPreview: ({ nativeSetDragImage }) =>
          disableNativeDragPreview({ nativeSetDragImage }),
        onDragStart: ({ location }) => {
          const { clientX, clientY } = location.initial.input
          const box = element.getBoundingClientRect()
          grip.current = {
            x: clientX - box.left,
            y: clientY - box.top,
            width: box.width,
          }
          pointer.current = location.current.input
          setDragging(true)
        },
        onDrag: ({ location }) => {
          pointer.current = location.current.input
          if (preview.current) {
            place(preview.current, pointer.current, grip.current)
          }
        },
        onDropTargetChange: ({ location }) => {
          const [target] = location.current.dropTargets
          setRemoving(target !== undefined && offCanvas.is(target.data))
        },
        onDrop: () => {
          setRemoving(false)
          setDragging(false)
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => accepts(source.data),
        getData: ({ input }) =>
          attachClosestEdge(zone.data(id), {
            element,
            input,
            allowedEdges: ["top", "bottom"],
          }),
        onDrag: ({ self, source }) =>
          setDropEdge(
            kind.from(source.data) === id
              ? null
              : extractClosestEdge(self.data),
          ),
        onDragLeave: () => setDropEdge(null),
        onDrop: () => setDropEdge(null),
      }),
    )
  }, [id, kind, zone, accepts])

  return {
    ref,
    handleRef,
    dragging,
    dropEdge,
    removing,
    /** For the copy: puts it under the pointer as soon as it is drawn. */
    previewRef: (element: HTMLElement | null) => {
      preview.current = element
      if (element) place(element, pointer.current, grip.current)
    },
  }
}

export type SortableItem = ReturnType<typeof useSortableItem>

type Input = { clientX: number; clientY: number }

/** Where in the item it was picked up, and how wide it is. */
type Grip = { x: number; y: number; width: number }

/** Puts the copy under the pointer, held where the item was picked up. */
function place(element: HTMLElement, { clientX, clientY }: Input, grip: Grip) {
  element.style.width = `${grip.width}px`
  element.style.transform = `translate(${clientX - grip.x}px, ${clientY - grip.y}px)`
}
